import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { computeBatchPreview } from '../../calc/preview'
import { parseAmount } from '../../validation'
import { formatCzk, formatUnits } from './format'

/** Ruční výjimka jednoho účastníka (precedent Šenkeřík). Prázdná pole = bez override. */
export interface OverrideValue {
  priceOverride?: number
  unitsOverride?: number
}

export interface OverrideTarget {
  depositId: string
  clientName: string
  /** Vklad použitý pro výpočet jednotek (amountReceived ?? amount). */
  amount: number
  /** Cena z ocenění — default, pokud není priceOverride. */
  unitPrice: number
}

interface OverrideDialogProps {
  open: boolean
  target: OverrideTarget | null
  existing?: OverrideValue
  onSave: (override: OverrideValue | null) => void
  onClose: () => void
}

/**
 * Ruční výjimka ceny/jednotek u jednoho účastníka přepočtu. Zadaná cena nahradí
 * cenu ocenění, zadané jednotky mají přednost před cenou (viz calc/preview).
 */
export function OverrideDialog({ open, target, existing, onSave, onClose }: OverrideDialogProps) {
  const [priceText, setPriceText] = useState('')
  const [unitsText, setUnitsText] = useState('')

  // Předvyplnit stávajícím overridem při každém otevření.
  useEffect(() => {
    if (open) {
      setPriceText(existing?.priceOverride !== undefined ? String(existing.priceOverride) : '')
      setUnitsText(existing?.unitsOverride !== undefined ? String(existing.unitsOverride) : '')
    }
  }, [open, existing])

  const price = parseAmount(priceText)
  const units = parseAmount(unitsText)
  const priceError = priceText.trim() !== '' && !(price !== null && price > 0)
  const unitsError = unitsText.trim() !== '' && !(units !== null && units > 0)

  const hasPrice = price !== null && price > 0
  const hasUnits = units !== null && units > 0

  // Náhled přidělených jednotek pro zadané hodnoty — přes calc s jediným účastníkem.
  const previewUnits = useMemo(() => {
    if (!target || priceError || unitsError) return null
    const preview = computeBatchPreview({
      totalUnitsBefore: 0,
      unitPrice: target.unitPrice,
      participants: [
        {
          depositId: target.depositId,
          clientId: '',
          clientName: '',
          amount: target.amount,
          ...(hasPrice ? { priceOverride: price } : {}),
          ...(hasUnits ? { unitsOverride: units } : {}),
        },
      ],
    })
    return preview.participants[0]?.units ?? null
  }, [target, price, units, hasPrice, hasUnits, priceError, unitsError])

  const handleSave = () => {
    if (priceError || unitsError) return
    const override: OverrideValue = {}
    if (hasPrice) override.priceOverride = price as number
    if (hasUnits) override.unitsOverride = units as number
    // Obě pole prázdná = žádná výjimka.
    onSave(
      override.priceOverride === undefined && override.unitsOverride === undefined ? null : override
    )
  }

  const hasExisting =
    existing !== undefined &&
    (existing.priceOverride !== undefined || existing.unitsOverride !== undefined)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ruční výjimka</DialogTitle>
      <DialogContent>
        {target && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {target.clientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                vklad {formatCzk(target.amount)} · cena ocenění {formatUnits(target.unitPrice)}
              </Typography>
            </Box>

            <Alert severity="info">
              Ruční výjimka (precedent Šenkeřík): zadaná cena nahradí cenu ocenění, zadané jednotky
              mají přednost před cenou.
            </Alert>

            <TextField
              size="small"
              label="Cena jednotky (override)"
              value={priceText}
              onChange={(e) => setPriceText(e.target.value)}
              error={priceError}
              helperText={priceError ? 'Cena musí být větší než 0' : 'Prázdné = cena z ocenění'}
              placeholder={formatUnits(target.unitPrice)}
            />

            <TextField
              size="small"
              label="Jednotky (override)"
              value={unitsText}
              onChange={(e) => setUnitsText(e.target.value)}
              error={unitsError}
              helperText={unitsError ? 'Jednotky musí být větší než 0' : 'Prázdné = spočítat z ceny'}
            />

            {previewUnits !== null && (
              <Typography variant="body2">
                Přidělené jednotky: <b>{formatUnits(previewUnits)}</b>
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose}>
          Zrušit
        </Button>
        {hasExisting && (
          <Button variant="text" color="error" onClick={() => onSave(null)}>
            Odebrat override
          </Button>
        )}
        <Button variant="contained" onClick={handleSave} disabled={priceError || unitsError}>
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
